<script setup lang="ts">
import { computed, InjectionKey, provide, ref } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import ConfirmationContent from './ConfirmationContent.vue';
import type { ConfirmationModalInjectionKeyType, ConfirmationModalOptions } from './types';
import Button from '@/components/buttons/Button.vue';

interface ConfirmationModalProps {
  defaultOptions: ConfirmationModalOptions;
  injectionKey?: InjectionKey<ConfirmationModalInjectionKeyType>;
}

const props = defineProps<ConfirmationModalProps>();

const resolve = ref<(value: boolean) => void>();

const options = ref<ConfirmationModalOptions>(props.defaultOptions);

const confirm = async () => {
  if (resolve.value) {
    if (options.value.onConfirm) {
      await options.value.onConfirm();
    }
    resolve.value(true);
  }

  resolve.value = undefined;
};

const cancel = async () => {
  if (resolve.value) {
    if (options.value.onCancel) {
      await options.value.onCancel();
    }
    resolve.value(false);
  }

  resolve.value = undefined;
};

const openDialog = (
  promise: (value: boolean | PromiseLike<boolean>) => void,
  confirmationOptions: ConfirmationModalOptions = {},
) => {
  resolve.value = promise;
  options.value = { ...props.defaultOptions, ...confirmationOptions };
};

provide(props.injectionKey, { openDialog });

const isOpen = computed(() => Boolean(resolve.value));
</script>

<template>
  <Dialog
    v-model="isOpen"
    :size="options.size"
    :hideClose="options.hideClose"
    @update:modelValue="(shouldOpen) => !shouldOpen && cancel()"
  >
    <template #title>
      <v-icon v-if="options.titleIcon" class="mr-1">
        {{ options.titleIcon }}
      </v-icon>

      {{ options.title }}
    </template>

    <template #text>
      <ConfirmationContent
        v-if="options.contentData"
        v-bind="options.contentData"
      ></ConfirmationContent>
      <p v-if="options.text" class="text-pre-line">{{ options.text }}</p>
    </template>

    <template #actions>
      <Button v-if="!options.hideCancelButton" variant="text" @click="cancel">{{
        options.cancelButton
      }}</Button>

      <Button v-if="options.confirmationButtonType === 'Delete'" variant="special" @click="confirm">
        {{ options.confirmButton }}
      </Button>

      <Button
        v-if="options.confirmationButtonType === 'Leave'"
        variant="secondary"
        @click="confirm"
      >
        {{ options.confirmButton }}
      </Button>

      <Button
        v-if="options.confirmationButtonType === 'Save'"
        variant="primary"
        :label="options.confirmButton"
        @click="confirm"
      />
    </template>
  </Dialog>
  <slot />
</template>
